import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  DAY_GATED_TRIGGERS,
} from "../CONSTANTS/reviewTriggers";
import {
  isAlreadyReviewed,
  isNormalizationShown,
  isShownThisSession,
  getPromptCountThisMonth,
  getLastPromptShownAt,
  getLastPromptTrigger,
  recordPromptShown,
  markAlreadyReviewed,
  clearAlreadyReviewed,
} from "../utils/reviewStorageUtils";
import { reviewActions } from "../store/review-slice";
import { trackIntercomEvent } from "../utils/intercomUtils";

const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const MAX_PROMPTS_PER_MONTH = 2;

// Deep-link to the app's Shopify App Store listing with the "Write a review"
// modal pre-opened. Used when the merchant clicks "Yes, it's helpful".
const DEFAULT_APP_STORE_REVIEW_URL =
  "https://apps.shopify.com/inventory-forecasting-hero#modal-show=WriteReviewModal&st_campaign=rate-app&st_source=admin-web&utm_campaign=installed&utm_source=shopify";

// Central decision hook for the In-App Review Flow.
//
// Supports three rendering patterns from the Figma design:
//   1. Standalone modal  (Forecast Report, Best Sellers, Sync)  → triggerReview()
//   2. Combined card     (Export to CSV)                         → canShowPrompt() + acknowledgeInlineShown()
//   3. Inline CTA strip  (PO success modal)                      → canShowPrompt() + acknowledgeInlineShown()
//
// All "should I show?" rules live here so pages never reimplement them.

// Backend source of truth from GET /api/account/details (app_store_reviewed).
const isAppStoreReviewedOnServer = (userDetails) =>
  userDetails?.app_store_reviewed === true;

// Suppress all review UI when the server says reviewed, or this browser already
// recorded "Yes" in localStorage (kept in sync with app_store_reviewed below).
const isReviewSuppressed = (userDetails) =>
  isAppStoreReviewedOnServer(userDetails) || isAlreadyReviewed();

const useReviewFlow = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const { isOnboardingCompleted } = useSelector((state) => state.jobstatus);

  // Keep review_already_reviewed aligned with GET /api/account/details.
  useEffect(() => {
    if (!userDetails) return;

    if (isAppStoreReviewedOnServer(userDetails)) {
      markAlreadyReviewed();
    } else if (userDetails.app_store_reviewed === false) {
      clearAlreadyReviewed();
    }
  }, [userDetails]);

  // Pure decision tree — no side effects. Safe to call from render().
  const canShowPrompt = useCallback(
    (trigger) => {
      // 0. Wait until user details are loaded — needed for installation_date.
      if (!userDetails || !userDetails.installation_date) return false;

      // 1. Server reviewed flag, or user clicked "Yes" on this browser.
      if (isReviewSuppressed(userDetails)) return false;

      // 2. Never during onboarding.
      if (!isOnboardingCompleted) return false;

      // 3. Once-per-session lock — covers all 3 UI patterns combined.
      if (isShownThisSession()) return false;

      // 4. Monthly cap — combined count across all UI patterns.
      if (getPromptCountThisMonth() >= MAX_PROMPTS_PER_MONTH) return false;

      // 5. 4-day cooldown since last prompt of any kind.
      const lastShown = getLastPromptShownAt();
      if (lastShown && Date.now() - lastShown.getTime() < FOUR_DAYS_MS) return false;

      // 6. Day-gated triggers are suppressed during the first 24h after install.
      if (DAY_GATED_TRIGGERS.has(trigger)) {
        const installDate = new Date(userDetails.installation_date);
        if (
          !isNaN(installDate.getTime()) &&
          Date.now() - installDate.getTime() < ONE_DAY_MS
        ) {
          return false;
        }
      }

      // 7. Don't fire the same trigger back-to-back.
      if (trigger === getLastPromptTrigger()) return false;

      return true;
    },
    [userDetails, isOnboardingCompleted]
  );

  // Used by standalone-modal triggers (Forecast / Best Sellers / Sync).
  // Runs canShowPrompt, and if eligible, persists the "shown" state and opens the modal.
  const triggerReview = useCallback(
    (trigger) => {
      if (!canShowPrompt(trigger)) return false;
      recordPromptShown(trigger);
      dispatch(reviewActions.showPrompt(trigger));
      return true;
    },
    [canShowPrompt, dispatch]
  );

  // Used by inline patterns (PO modal CTA strip, Export combined card).
  // The caller has already decided to render the inline element via canShowPrompt();
  // this records the prompt against the per-month / cooldown / session-lock rules.
  // Idempotent within the same session because the session lock blocks any further prompts.
  const acknowledgeInlineShown = useCallback((trigger) => {
    recordPromptShown(trigger);
  }, []);

  // Onboarding normalization message (Figma image 1).
  const shouldShowNormalization = useCallback(() => {
    if (!userDetails) return false;
    if (!isOnboardingCompleted) return false;
    if (isReviewSuppressed(userDetails)) return false;
    if (isNormalizationShown()) return false;
    return true;
  }, [userDetails, isOnboardingCompleted]);

  // Handles the three possible responses to any review prompt
  // (standalone modal, combined card, or inline CTA).
  const submitFeedback = useCallback(
    (trigger, response) => {
      if (response === "helpful") {
        markAlreadyReviewed();
        const appStoreUrl =
          process.env.REACT_APP_SHOPIFY_APP_STORE_URL ||
          DEFAULT_APP_STORE_REVIEW_URL;
        window.open(appStoreUrl, "_blank", "noopener,noreferrer");
      } else if (response === "not_helpful") {
        try {
          trackIntercomEvent("review_not_helpful", { trigger });
        } catch (e) {
          // ignore — intercom may not be initialised yet
        }
        try {
          if (window.Intercom) {
            window.Intercom("show");
          }
        } catch (e) {
          // ignore
        }
      }
      // 'dismissed' — no extra side effects; modal/card just closes.

      dispatch(reviewActions.hidePrompt());
      dispatch(reviewActions.hideExportSuccessWithReview());
    },
    [dispatch]
  );

  return {
    canShowPrompt,
    triggerReview,
    acknowledgeInlineShown,
    shouldShowNormalization,
    submitFeedback,
  };
};

export default useReviewFlow;
